import './styles/OptionModal.scss';

import { Grid } from '@mui/material';
import { Option, Product, UpdateOption } from '@thro-types/leihbar';

import { Formik } from 'formik';
import { omit } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useOptionMutation } from 'api/hooks/leihbar';
import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { validateForm } from 'utils/FormErrorHelper';

import { uploadAttachmentToS3 } from '../../helper';
import { OptionInformation } from './components/OptionInformation';
import { initialValues, optionValidationSchema } from './validation';

interface Props {
  closeHandler: Function;
  setDisabledCancel: Function;
  product: Product;
  option: Option | undefined;
  postOptionCreate: Function;
}

export function OptionModal(props: Props) {
  const [submitting, setSubmitting] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { create, update } = useOptionMutation(props.product.id);

  const handleSaveClick = async (values: UpdateOption & { id?: number }) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }

    if (typeof values.image !== 'string') {
      try {
        let uploadResult = await uploadAttachmentToS3({ file: values.image, type: 'OptionImage' });
        values.image = uploadResult[0];
      } catch (error) {
        dispatch(
          AlertActions.error(t(translations.pages.leihbar.option.alerts.failedImageUpload()))
        );
        setSubmitting(false);
        if (props.setDisabledCancel) {
          props.setDisabledCancel(false);
        }
        return;
      }
    }

    const omitFields = ['id'] as const;
    let saveBody = omit(values, omitFields);
    saveBody.productId = props.product.id;

    if (values.id) {
      try {
        await update.mutateAsync({ id: values.id, data: saveBody });
        dispatch(
          AlertActions.success(t(translations.pages.leihbar.option.alerts.successfulUpdate()))
        );
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.option.alerts.failedUpdate())));
        setSubmitting(false);
      }
    } else {
      try {
        const option = await create.mutateAsync(saveBody);
        props.postOptionCreate(option.id, option);

        dispatch(
          AlertActions.success(t(translations.pages.leihbar.option.alerts.successfulCreate()))
        );
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.option.alerts.failedCreate())));
        setSubmitting(false);
      }
    }
    props.closeHandler();
  };

  const steps = ['Information'];

  const SettingsModalStep = ({ step }) => {
    switch (step) {
      case 0:
        return <OptionInformation />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="optionModal">
      <Formik
        initialValues={initialValues(props.product, props.option)}
        //@ts-ignore
        onSubmit={values => handleSaveClick(values)}
        validationSchema={optionValidationSchema(t)}
        validate={values => {
          return validateForm(values, optionValidationSchema(t), dispatch);
        }}
      >
        <MultistepForm
          submitting={submitting}
          steps={steps}
          onCancel={props.closeHandler}
          hideStepper
        >
          {activeStep => (
            <Grid container className="optionModal__body">
              <SettingsModalStep step={activeStep} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
